import { GameObject, HelperFunctions } from "../../tsthree";
import { tsthreeConfig } from "../../config/tsthreeConfig";
import { NineSlicePlane, Sprite, Text, TextStyle } from "pixi.js";
import { GAME_FONT, GAME_FONT_BOLD } from "../Constants/Constants";
import { buttonify } from "../../engine/HelperFunctions/buttonify";
import { INotification } from "../Types/INotification";
import { Easing } from "@tweenjs/tween.js";
import { AUDIO_TYPES, AudioSingleton } from "../../engine/AudioSingleton";
import { formatCurrentTime } from "../../engine/HelperFunctions/formatCurrentTime";

interface INotificationPoolObject {
    currentNotification: INotification | null;
    container: GameObject;
    bg: NineSlicePlane;
    titleIcon: Sprite;
    titleText: Text;
    mainText: Text;
    timestampText: Text;
    isAnimating: boolean;
}

const notificationHeight = 130;
const notificationWidth = 200;

export class NotificationsColumn extends GameObject {

    private _pool: INotificationPoolObject[] = [];
    private _animTimer = 0;
    private _animatingShift: boolean = false;

    public hideNotification(
        _id: GameObject | string
    ): void {
        let notificationInd;
        if(typeof _id === "string") {
            notificationInd =
                this._pool.findIndex((e) => e.currentNotification?.id === _id);
        } else if(_id instanceof GameObject) {
            notificationInd =
                this._pool.findIndex((e) => e.container === _id);
        }
        if(notificationInd === -1) {
            console.error("Could not find notification to remove of id %s", _id);
            return;
        }

        this._animatingShift = true;

        this._pool.push(
            ...this._pool.splice(notificationInd, 1)
        );
        this._pool[this._pool.length - 1].currentNotification = null;
        this._pool[this._pool.length - 1].container.visible = false;
        const numOfVisible = this._pool
            .filter((e) => e.container.visible && e.currentNotification)
            .length;
        const duration = 200;
        const promises = [];
        for(let i = 0; i < numOfVisible; i++) {
            const poolObj = this._pool[i];
            if(i === 0) {
                promises.push(HelperFunctions.TWEENVec2AsPromise(
                    poolObj.container,
                    {
                        x: 0,
                        y: (notificationHeight * -i) - (8 * i),
                    },
                    Easing.Linear.None,
                    duration,
                ).promise);
            } else {
                promises.push(HelperFunctions.TWEENVec2AsPromise(
                    poolObj.container,
                    {
                        x: 0,
                        y: (notificationHeight * -i) - (8 * i),
                    },
                    Easing.Linear.None,
                    duration,
                ).promise);
            }
        }
        Promise.allSettled(promises).then(() => {
            this._animatingShift = false;
        });
    }

    private getFreeIndFromPool(): number {
        return this._pool.findIndex(
            (e) => !e.currentNotification && !e.container.visible
        );
    }

    public showNotification(_notification: INotification): void {
        const ind = this.getFreeIndFromPool();
        const notificationObj = this._pool[ind];
        if(!notificationObj) {
            console.warn("Could not show notification %o", _notification);
            return;
        }

        notificationObj.currentNotification = Object.assign({}, _notification);
        notificationObj.mainText.text =
            notificationObj.currentNotification.mainStr;
        notificationObj.titleText.text =
            notificationObj.currentNotification.titleStr;
        notificationObj.timestampText.text =
            formatCurrentTime();
        notificationObj.titleIcon.texture =
            ENGINE.getTexture(notificationObj.currentNotification.iconKey);
        notificationObj.titleIcon.width =
            notificationObj.titleIcon.height = 16;
        notificationObj.container.visible = true;

        // notificationObj.container.alpha = 0;
        // HelperFunctions.TWEENAsPromise(
        //     notificationObj.container,
        //     "alpha",
        //      1,
        //     Easing.Quadratic.Out,
        //     200,
        //     null,
        //     () => {
        //         notificationObj.container.alpha =
        //             (Math.round(notificationObj.container.alpha * 6) / 6);
        //     }
        // );
        notificationObj.container.position.x = 250;
        notificationObj.container.position.y = (notificationHeight * -ind) - (8 * ind);
        notificationObj.isAnimating = true;

        AudioSingleton.playSound(
            "confirm",
            {
                volume: 0.5,
                audioType: AUDIO_TYPES.SFX
            }
        );

        HelperFunctions.TWEENVec2AsPromise(
            notificationObj.container.position,
            {
                x:  0,
                y: notificationObj.container.position.y,
            },
            Easing.Quadratic.Out,
            600,
            () => {
                return true;
            }
        ).promise.then(() => {
            notificationObj.isAnimating = false;
        });
    }

    onStep(_dt: number) {
        super.onStep(_dt);
        if(this._animatingShift) return;
        this._animTimer += _dt;
        this._pool.forEach((e, i) => {
            if(
                !e.container.visible &&
                !e.currentNotification
            ) {
                return;
            }

            if(i === 0 && !e.isAnimating) {
                e.container.position.set(
                    -0,
                    (notificationHeight * -i) - (8 * i) +
                        (Math.ceil(Math.sin(this._animTimer * 0.05) * 100) / 100),
                );
            } else if(!e.isAnimating) {
                e.container.position.set(
                    e.container.position.x,
                    (notificationHeight * -i) - (8 * i),
                );
            }

        });
    }

    constructor() {
        super("NotificationsColumn");
        const notificationsPoolSize = Math.floor(
            (tsthreeConfig.height - 38) / notificationHeight
        ) + 1;

        for(let i = 0; i < notificationsPoolSize; i++) {
            const ii = i;
            const notification = new GameObject();
            notification.position.set(0, 0);
            this.addChild(notification);

            const bg = new NineSlicePlane(ENGINE.getTexture("popup_nineslice"));
            bg.width = notificationWidth;
            bg.height = notificationHeight;
            bg.position.set(
                (-bg.width) - 8,
                -notificationHeight - 40
            );
            notification.addChild(bg);

            const titleIcon = new Sprite(ENGINE.getTexture("mail-icon"));
            titleIcon.position.set(4, 6);
            titleIcon.width =
                titleIcon.height = 16;
            bg.addChild(titleIcon);

            const titleText = new Text(
                "E-Mail",
                new TextStyle({
                    fontFamily: GAME_FONT_BOLD,
                    fontSize: 11,
                })
            );
            titleText.position.set(24, 7);
            bg.addChild(titleText);

            const timestampText = new Text("4:20pm",
                new TextStyle({
                    fontFamily: GAME_FONT,
                    fontSize: 11,
                    align: "right"
                })
            );
            timestampText.anchor.set(1, 0);
            timestampText.position.set(bg.width - 4, 7);
            bg.addChild(timestampText);

            const text = new Text(
                "This is a test notification, so here's a bunch of meaningless text to pad it out and test the limits of these kinds of popups. It'll also acts as a kind-of styleunit so that'll be nice.",
                new TextStyle({
                    fontFamily: GAME_FONT,
                    fontSize: 11,
                    wordWrap: true,
                    wordWrapWidth: bg.width - 12
                })
            );
            text.position.set(6, 26);
            bg.addChild(text);

            const gotItText = new Text(
                "Got it",
                new TextStyle({
                    fontFamily: GAME_FONT,
                    fontSize: 11,
                    fill: 0x0000ff,
                    align: "right"
                })
            );
            gotItText.anchor.set(1, 1);
            gotItText.position.set(
                bg.width - 12,
                bg.height - 4
            );
            bg.addChild(gotItText);
            buttonify(gotItText, {
                onFire: () => {
                    // close notification
                    const item =
                        this._pool.find(
                            (e) => e.container === notification
                        );
                    if(
                        item?.currentNotification &&
                        !item?.isAnimating
                    ) {
                        this.hideNotification(
                            notification
                        );
                    }
                }
            });

            notification.visible = false;
            this._pool.push(
                {
                    currentNotification: null,
                    timestampText: timestampText,
                    bg: bg,
                    container: notification,
                    titleIcon: titleIcon,
                    titleText: titleText,
                    mainText: text,
                    isAnimating: false,
                }
            );
        }
    }
}